'use client'
import { useRef, useEffect, useState, startTransition } from 'react'
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion'
import { useLanguage } from '@/lib/context/LanguageContext'
import { assetUrl } from '@/lib/assetUrl'

const ease = [0.22, 1, 0.36, 0.98] as const

// The poster is the LCP element and identical on every viewport — resolve it
// at render time (SSR included) so the browser discovers it from the initial
// HTML instead of waiting for hydration. Only the video src is
// viewport-dependent and must stay client-side.
const POSTER_SRC = assetUrl('hero/hero-poster.jpg')

// ── Hero ──────────────────────────────────────────────────────────────────────
// The hero is a 5-second brand-reveal clip that plays once. The video opens
// dark with iridescent stars and resolves to the white "trend" wordmark on the
// final frame, where it freezes. Tagline + primary CTA fade in around the same
// time the wordmark becomes legible, then a soft scroll hint follows.
export default function Hero() {
  const { t, isRTL } = useLanguage()
  const sectionRef = useRef<HTMLElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const reduce = useReducedMotion()

  // On mobile viewports use the portrait-format video (9:16) so the full brand
  // wordmark is visible without awkward cropping. Desktop keeps the landscape clip.
  const [videoSrc, setVideoSrc] = useState('')
  useEffect(() => {
    const isMobile = window.matchMedia('(max-width: 639px)').matches
    startTransition(() => {
      setVideoSrc(isMobile ? assetUrl('hero/hero-mobile.mp4') : assetUrl('hero/hero.mp4'))
    })
  }, [])

  // Programmatically trigger play — fires once videoSrc is resolved on the client.
  // Starting with '' prevents the wrong video from loading before the breakpoint check.
  useEffect(() => {
    const v = videoRef.current
    if (!v || !videoSrc) return
    v.load()
    v.play().catch(() => {})
  }, [videoSrc])

  // Reduced motion: pause the brand video so users only see the poster frame.
  useEffect(() => {
    if (reduce) videoRef.current?.pause()
  }, [reduce])

  // Scroll-driven parallax — content slides up and fades as the page scrolls,
  // revealing the next section underneath. Same pattern as the old hero.
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  })
  const contentY = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [0, -120])
  const contentOpacity = useTransform(scrollYProgress, [0, 0.55], reduce ? [1, 1] : [1, 0])

  // When reduced motion is on, drop the entrance delay/translate so the copy
  // is visible immediately on the static poster frame.
  const delay = (d: number) => (reduce ? 0 : d)
  const enterY = (y: number) => (reduce ? 0 : y)

  // Entrance choreography must not hide the copy in the served HTML: framer
  // SSRs `initial` as an inline style, which shipped the page's only <h1>
  // (and the primary CTA) as opacity:0 text — invisible to no-JS visitors
  // and to crawlers that snapshot early (2026-07 SEO audit flag). So the
  // server renders them visible ('ssr'), and the fade-in runs only after
  // hydration: snap to hidden, then play the original timeline. The snap
  // happens during the video's dark intro, long before the wordmark
  // resolves, so it isn't perceptible.
  const [entrance, setEntrance] = useState<'ssr' | 'hidden' | 'in'>('ssr')
  useEffect(() => {
    if (reduce) return
    let inId: number | undefined
    const hideId = requestAnimationFrame(() => {
      setEntrance('hidden')
      inId = requestAnimationFrame(() => setEntrance('in'))
    })
    return () => {
      cancelAnimationFrame(hideId)
      if (inId !== undefined) cancelAnimationFrame(inId)
    }
  }, [reduce])

  return (
    <section
      ref={sectionRef}
      id="hero"
      className="relative min-h-svh flex flex-col bg-white overflow-x-clip"
    >
      {/* Brand-reveal video — plays once, freezes on the final wordmark frame */}
      <video
        ref={videoRef}
        muted
        playsInline
        // Decorative brand loop, no dialogue — hide from the a11y tree
        // (also exempts it from the captions-track audit).
        aria-hidden="true"
        preload="none"
        poster={POSTER_SRC}
        onEnded={() => videoRef.current?.pause()}
        className="absolute inset-0 w-full h-full object-cover"
        src={videoSrc || undefined}
      />

      {/* ── Parallax content layer ── */}
      <motion.div
        className="relative z-10 flex-1 flex flex-col"
        style={{ y: contentY, opacity: contentOpacity }}
      >
        {/* Spacer pushes the copy stack to the lower portion of the viewport,
            sitting just under where the "trend" wordmark resolves in the video. */}
        <div className="flex-1" />

        <div className="flex flex-col items-center text-center px-4 sm:px-8 pb-[14vh] sm:pb-[12vh]">
          {/* Tagline — appears around the moment the wordmark becomes legible (~2.3s).
              Brand blue so it reads on the white final frame. */}
          <motion.h1
            className="font-display font-bold text-base sm:text-lg md:text-xl leading-relaxed tracking-tight"
            style={{ color: '#0335b7' }}
            initial={false}
            animate={entrance}
            variants={{
              ssr: { opacity: 1, y: 0 },
              hidden: { opacity: 0, y: enterY(18), transition: { duration: 0 } },
              in: { opacity: 1, y: 0, transition: { duration: 2.4, delay: delay(2.3), ease } },
            }}
          >
            {t.hero.h1a}
          </motion.h1>

          {/* Primary CTA — gradient hugs the wordmark palette */}
          <motion.div
            className="mt-7"
            initial={false}
            animate={entrance}
            variants={{
              ssr: { opacity: 1, y: 0 },
              hidden: { opacity: 0, y: enterY(18), transition: { duration: 0 } },
              in: { opacity: 1, y: 0, transition: { duration: 2.4, delay: delay(2.9), ease } },
            }}
          >
            <a
              href="#contact"
              className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5"
              style={{
                background: 'linear-gradient(90deg, #0335b7 0%, #00b4d8 100%)',
                boxShadow:
                  '0 12px 32px -10px rgba(3,53,183,0.45), 0 4px 18px -6px rgba(0,180,216,0.35)',
              }}
            >
              {t.hero.cta1}
              <span aria-hidden className="text-base leading-none">
                {isRTL ? '←' : '→'}
              </span>
            </a>
          </motion.div>
        </div>
      </motion.div>

      {/* Subtle scroll hint — fades in last, gently pulses */}
      <motion.div
        aria-hidden
        className="absolute bottom-5 inset-x-0 z-10 flex justify-center pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: reduce ? 0.6 : [0, 0.6, 0.6] }}
        transition={{ duration: reduce ? 0 : 0.8, delay: delay(5.5), ease: 'easeOut' }}
      >
        <motion.span
          className="text-[10px] font-semibold uppercase"
          style={{ color: 'rgba(3,53,183,0.4)', letterSpacing: '0.25em' }}
          animate={reduce ? {} : { y: [0, 6, 0], opacity: [0.6, 0.85, 0.6] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut', delay: delay(6) }}
        >
          {t.hero.scroll}
        </motion.span>
      </motion.div>
    </section>
  )
}
